/**
 * Alpha Vantage API Service
 * Provides interfaces for searching symbols and fetching market data
 */

import { Cache, dataCache } from './cache';

// Base URL for Alpha Vantage API
const API_URL = 'https://www.alphavantage.co/query';

// Cache for quote data
const quoteCache = new Cache<any>({ ttl: 60 * 1000 }); // 60 seconds TTL
const searchCache = new Cache<any>({ ttl: 24 * 60 * 60 * 1000 }); // 24 hours TTL
// Caches for fundamental data
const overviewCache = new Cache<any>({ ttl: 60 * 60 * 1000 }); // 1 hour TTL
const incomeStatementCache = new Cache<any>({ ttl: 60 * 60 * 1000 });
const balanceSheetCache = new Cache<any>({ ttl: 60 * 60 * 1000 });
const cashFlowCache = new Cache<any>({ ttl: 60 * 60 * 1000 });

class AlphaVantageService {
  private apiKey: string = 'demo'; // Default to demo key, should be replaced with real key

  /**
   * Set the API key for Alpha Vantage
   * @param key API key
   */
  setApiKey(key: string): void {
    this.apiKey = key;
  }

  /**
   * Search for a symbol by keyword
   * @param keywords Search terms
   * @returns Search results
   */
  async searchSymbol(keywords: string): Promise<any> {
    const cacheKey = `search_${keywords.toLowerCase()}`;
    
    // Check cache first
    const cachedResult = searchCache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }
    
    const params = new URLSearchParams({
      function: 'SYMBOL_SEARCH',
      keywords,
      apikey: this.apiKey
    });
    
    try {
      const response = await fetch(`${API_URL}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Alpha Vantage API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the result
      searchCache.set(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Error searching symbol with Alpha Vantage:', error);
      throw error;
    }
  }

  /**
   * Get quote for a symbol
   * @param symbol Stock symbol
   * @returns Quote data
   */
  async getQuote(symbol: string): Promise<any> {
    const cacheKey = `quote_${symbol.toUpperCase()}`;
    
    // Check cache first
    const cachedQuote = quoteCache.get(cacheKey);
    if (cachedQuote) {
      return cachedQuote;
    }
    
    const params = new URLSearchParams({
      function: 'GLOBAL_QUOTE',
      symbol,
      apikey: this.apiKey
    });
    
    try {
      const response = await fetch(`${API_URL}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Alpha Vantage API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the result
      quoteCache.set(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Error getting quote from Alpha Vantage:', error);
      throw error;
    }
  }

  /**
   * Validate if a symbol exists
   * @param symbol Symbol to validate
   * @returns True if symbol exists, false otherwise
   */
  async validateSymbol(symbol: string): Promise<boolean> {
    try {
      const data = await this.getQuote(symbol);
      
      // Check if we got valid data back
      if (data && data['Global Quote'] && data['Global Quote']['01. symbol']) {
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error validating symbol ${symbol}:`, error);
      return false;
    }
  }

  /**
   * Calculate Z-Score for a symbol
   * This is a simplified implementation that returns mock data
   * @param symbol Stock symbol
   * @returns Z-Score value
   */
  async calculateZScore(symbol: string): Promise<number> {
    // This would normally do actual calculations
    // For now, generate a realistic z-score between -3 and 3
    return (Math.random() * 6) - 3;
  }

  /**
   * Get volatility for a symbol
   * @param symbol Stock symbol
   * @returns Volatility value
   */
  async getVolatility(symbol: string): Promise<number> {
    // This would normally calculate historical volatility
    // For now, generate a realistic volatility between 0.1 and 0.6
    return 0.1 + Math.random() * 0.5;
  }

  /**
   * Get correlation between two symbols
   * @param symbolA First symbol
   * @param symbolB Second symbol
   * @returns Correlation coefficient between -1 and 1
   */
  async getCorrelation(symbolA: string, symbolB: string): Promise<number> {
    // This would normally calculate correlation coefficient
    // For now, generate a realistic correlation between -0.9 and 0.9
    return (Math.random() * 1.8) - 0.9;
  }
  /** Fetch company overview data */
  async getOverview(symbol: string): Promise<any> {
    const cacheKey = `overview_${symbol.toUpperCase()}`;
    const cached = overviewCache.get(cacheKey);
    if (cached) return cached;
    const params = new URLSearchParams({
      function: 'OVERVIEW',
      symbol,
      apikey: this.apiKey
    });
    try {
      const response = await fetch(`${API_URL}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Alpha Vantage API error: ${response.status}`);
      }
      const data = await response.json();
      overviewCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching overview for ${symbol}:`, error);
      throw error;
    }
  }

  /** Fetch income statement data */
  async getIncomeStatement(symbol: string): Promise<any> {
    const cacheKey = `income_${symbol.toUpperCase()}`;
    const cached = incomeStatementCache.get(cacheKey);
    if (cached) return cached;
    const params = new URLSearchParams({
      function: 'INCOME_STATEMENT',
      symbol,
      apikey: this.apiKey
    });
    try {
      const response = await fetch(`${API_URL}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Alpha Vantage API error: ${response.status}`);
      }
      const data = await response.json();
      incomeStatementCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching income statement for ${symbol}:`, error);
      throw error;
    }
  }

  /** Fetch balance sheet data */
  async getBalanceSheet(symbol: string): Promise<any> {
    const cacheKey = `balance_${symbol.toUpperCase()}`;
    const cached = balanceSheetCache.get(cacheKey);
    if (cached) return cached;
    const params = new URLSearchParams({
      function: 'BALANCE_SHEET',
      symbol,
      apikey: this.apiKey
    });
    try {
      const response = await fetch(`${API_URL}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Alpha Vantage API error: ${response.status}`);
      }
      const data = await response.json();
      balanceSheetCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching balance sheet for ${symbol}:`, error);
      throw error;
    }
  }

  /** Fetch cash flow data */
  async getCashFlow(symbol: string): Promise<any> {
    const cacheKey = `cashflow_${symbol.toUpperCase()}`;
    const cached = cashFlowCache.get(cacheKey);
    if (cached) return cached;
    const params = new URLSearchParams({
      function: 'CASH_FLOW',
      symbol,
      apikey: this.apiKey
    });
    try {
      const response = await fetch(`${API_URL}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Alpha Vantage API error: ${response.status}`);
      }
      const data = await response.json();
      cashFlowCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error fetching cash flow for ${symbol}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const alphaVantageService = new AlphaVantageService();
